// W33 weekly symptom trends across the caseload.
//
// Read-only view over the existing W33 worksheet: each participant's own
// symptoms, scored 0-10 from Baseline to Week 12, compared with their own
// baseline — never with another participant.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, Download, ExternalLink, Search, X } from "lucide-react";
import {
  CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { exportCsv } from "@/lib/exportCsv";

const WEEKS = ["B", ...Array.from({ length: 12 }, (_, i) => `W${i + 1}`)];
const WEEK_LONG = ["Baseline", ...Array.from({ length: 12 }, (_, i) => `Week ${i + 1}`)];
const LINE_COLORS = ["#047857", "#b45309", "#6d28d9"];

const FLAG = {
  high: { label: "Score 8+", cls: "bg-rose-600 text-white" },
  spike: { label: "Jump of 3+", cls: "bg-rose-100 text-rose-800" },
  no_improvement: { label: "No improvement by W4", cls: "bg-amber-100 text-amber-800" },
  stale: { label: "No update in 14 days", cls: "bg-stone-200 text-stone-700" },
};
const URGENT = new Set(["high", "spike"]);

const STATUS = {
  improved: { label: "Improved", cls: "text-emerald-700" },
  stable: { label: "Stable", cls: "text-stone-600" },
  worse: { label: "Worse", cls: "text-rose-700" },
};

// Static class strings — Tailwind cannot see a class built by interpolation.
function heat(v) {
  if (v == null) return "bg-stone-100 text-stone-300";
  if (v <= 2) return "bg-emerald-600 text-white";
  if (v <= 4) return "bg-lime-400 text-lime-950";
  if (v <= 6) return "bg-amber-300 text-amber-950";
  if (v <= 8) return "bg-orange-500 text-white";
  return "bg-rose-600 text-white";
}

/** One status per participant: the worst of their symptoms. */
function overallStatus(symptoms) {
  const s = symptoms.map((x) => x.status).filter(Boolean);
  if (!s.length) return null;
  if (s.includes("worse")) return "worse";
  if (s.includes("stable")) return "stable";
  return "improved";
}

/** Shorten a free-text symptom for tight spots; the full text stays in a tooltip. */
const clip = (text, n = 32) => (text.length > n ? `${text.slice(0, n - 1).trimEnd()}…` : text);

const fmt = (v) => (v == null ? "" : Number.isInteger(v) ? String(v) : v.toFixed(1));

function FlagChip({ f }) {
  const { t } = useTranslation();
  const d = FLAG[f];
  if (!d) return null;
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${d.cls}`}>{t(d.label)}</span>;
}

const SPARK_COLOR = { improved: "#047857", stable: "#78716c", worse: "#e11d48" };

/** Tiny baseline-to-latest line; blank weeks are skipped, not drawn as zero. */
function Sparkline({ series, status, w = 88, h = 22 }) {
  const pts = series.map((v, r) => (v == null ? null : [r, v])).filter(Boolean);
  if (pts.length < 2) return <span className="inline-block" style={{ width: w }} />;
  const x = (r) => 2 + (r / (series.length - 1)) * (w - 4);
  const y = (v) => 2 + (1 - v / 10) * (h - 4);
  const d = pts.map(([r, v], i) => `${i ? "L" : "M"}${x(r).toFixed(1)},${y(v).toFixed(1)}`).join("");
  const [lr, lv] = pts[pts.length - 1];
  const color = SPARK_COLOR[status] || SPARK_COLOR.stable;
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden="true">
      <line x1="2" x2={w - 2} y1={y(8)} y2={y(8)} stroke="#fecdd3" strokeDasharray="2 2" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx={x(lr)} cy={y(lv)} r="2.2" fill={color} />
    </svg>
  );
}

function TrendChart({ p }) {
  const { t } = useTranslation();
  const data = WEEKS.map((w, r) => {
    const row = { week: w };
    p.tracker.symptoms.forEach((s) => { row[`s${s.index}`] = s.series[r]; });
    return row;
  });
  const events = Object.fromEntries(p.tracker.events.map((e) => [e.week, e]));
  const TipBody = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const r = WEEKS.indexOf(label);
    const ev = events[r];
    return (
      <div className="max-w-xs rounded-md border border-[#E5DFD3] bg-white p-2 text-xs shadow">
        <div className="mb-1 font-semibold text-stone-800">{t(WEEK_LONG[r])}</div>
        {payload.map((x) => (
          <div key={x.dataKey} className="flex items-start justify-between gap-3">
            <span className="min-w-0 break-words" style={{ color: x.color }}>{x.name}</span>
            <b className="shrink-0 tabular-nums">{fmt(x.value)}</b>
          </div>
        ))}
        {ev?.trigger && <div className="mt-1 text-stone-600"><b>{t("Trigger")}:</b> {ev.trigger}</div>}
        {ev?.note && <div className="text-stone-600"><b>{t("Note")}:</b> {ev.note}</div>}
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 10, right: 16, bottom: 0, left: -16 }}>
        <CartesianGrid stroke="#EFE9DE" vertical={false} />
        <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#78716c" }} />
        <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 11, fill: "#78716c" }} />
        <ReferenceLine y={8} stroke="#e11d48" strokeDasharray="4 4" />
        {p.tracker.events.map((e) => (
          <ReferenceLine key={e.week} x={WEEKS[e.week]} stroke="#a8a29e" strokeDasharray="2 3" />
        ))}
        <Tooltip content={<TipBody />} />
        <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span title={v}>{clip(v)}</span>} />
        {p.tracker.symptoms.map((s, i) => (
          <Line key={s.index} dataKey={`s${s.index}`} name={s.name} stroke={LINE_COLORS[i]}
            strokeWidth={2} dot={{ r: 3 }} connectNulls isAnimationActive={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function SymptomTrendsPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [reviewOnly, setReviewOnly] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/trends/w33")
      .then((r) => setRows(r.data.participants || []))
      .catch((e) => { setErr(formatApiError(e.response?.data?.detail)); setRows([]); });
  }, []);

  const tracking = useMemo(() => (rows || []).filter((p) => p.tracker).map((p) => {
    const flags = p.tracker.flags;
    return {
      ...p, status: overallStatus(p.tracker.symptoms), flags,
      urgency: flags.filter((f) => URGENT.has(f)).length * 10 + flags.length,
    };
  }), [rows]);
  const notStarted = (rows || []).length - tracking.length;

  const totals = {
    improved: tracking.filter((p) => p.status === "improved").length,
    stable: tracking.filter((p) => p.status === "stable").length,
    worse: tracking.filter((p) => p.status === "worse").length,
    review: tracking.filter((p) => p.flags.length > 0).length,
  };

  const review = [...tracking].filter((p) => p.flags.length).sort((a, b) => b.urgency - a.urgency);

  const filtered = tracking.filter((p) => {
    if (status !== "all" && p.status !== status) return false;
    if (reviewOnly && !p.flags.length) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [p.name, p.email, p.participant_code].some((x) => (x || "").toLowerCase().includes(q));
  }).sort((a, b) => b.urgency - a.urgency || (a.name || "").localeCompare(b.name || ""));

  const sel = tracking.find((p) => p.id === selected) || null;

  // One row per participant symptom, matching the heatmap and its filters.
  const exportTrends = () => {
    const rowsOut = filtered.flatMap((p) => p.tracker.symptoms.map((s) => ({ p, s })));
    exportCsv(`Weekly symptom trends - ${new Date().toISOString().slice(0, 10)}`, [
      { label: "Participant", value: ({ p }) => p.name },
      { label: "Code", value: ({ p }) => p.participant_code },
      { label: "Email", value: ({ p }) => p.email },
      { label: "Stage", value: ({ p }) => p.assessment?.stage || "" },
      { label: "Symptom", value: ({ s }) => s.name },
      ...WEEK_LONG.map((label, r) => ({ label, value: ({ s }) => fmt(s.series[r]) })),
      { label: "Latest week", value: ({ s }) => (s.latest_week == null ? "" : WEEK_LONG[s.latest_week]) },
      { label: "Change vs baseline %", value: ({ s }) => s.change_pct ?? "" },
      { label: "Status", value: ({ s }) => (s.status ? STATUS[s.status].label : "") },
      { label: "Symptom flags", value: ({ s }) => s.flags.map((f) => FLAG[f].label).join("; ") },
      { label: "Participant flags", value: ({ p }) => p.flags.map((f) => FLAG[f].label).join("; ") },
      { label: "Triggers and notes", value: ({ p }) => p.tracker.events
          .map((e) => `${WEEK_LONG[e.week]}: ${[e.trigger, e.note].filter(Boolean).join(" - ")}`).join("; ") },
      { label: "Last saved", value: ({ p }) => (p.assessment?.updated_at || "").slice(0, 10) },
    ], rowsOut);
  };

  if (err) return (
    <AppShell>
      <p className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="trends-error">{err}</p>
    </AppShell>
  );

  return (
    <AppShell>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-serif text-3xl font-bold text-stone-900">
            <Activity className="h-6 w-6 text-emerald-800" /> {t("Weekly symptom trends")}
          </h1>
          <p className="text-sm text-stone-500">
            {t("From the W33 tracker. Each participant is compared with their own baseline, not with others.")}
            {user?.role === "doctor" ? ` ${t("Showing your assigned participants.")}` : ""}
          </p>
        </div>
        <button onClick={exportTrends} disabled={!filtered.length} data-testid="trends-export"
          title={t("Exports the participants currently shown in the table")}
          className="flex items-center gap-2 rounded-md border border-[#E5DFD3] bg-white px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40">
          <Download className="h-4 w-4" /> {t("Export CSV")}
        </button>
      </div>

      {rows === null ? (
        <p className="text-sm text-stone-400">{t("Loading…")}</p>
      ) : tracking.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#CAD2C5] bg-white p-12 text-center" data-testid="trends-empty">
          <Activity className="mx-auto h-10 w-10 text-emerald-700/40" />
          <p className="mt-3 font-serif text-xl text-stone-700">{t("No weekly tracker entries yet")}</p>
          <p className="text-sm text-stone-500">{t("Trends appear once participants fill in W33 in any assessment.")}</p>
        </div>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { k: "tracking", label: "Tracking", value: tracking.length, tone: "text-stone-800" },
              { k: "improved", label: "Improved", value: totals.improved, tone: "text-emerald-700" },
              { k: "stable", label: "Stable", value: totals.stable, tone: "text-stone-600" },
              { k: "worse", label: "Worse", value: totals.worse, tone: "text-rose-700" },
              { k: "review", label: "Needs review", value: totals.review, tone: "text-amber-700" },
              { k: "none", label: "Not started", value: notStarted, tone: "text-stone-400" },
            ].map((s) => (
              <div key={s.k} className="rounded-lg border border-[#E5DFD3] bg-white px-4 py-2.5" data-testid={`trends-stat-${s.k}`}>
                <div className="font-mono text-[11px] uppercase tracking-wider text-stone-400">{t(s.label)}</div>
                <div className={`font-serif text-2xl font-bold tabular-nums ${s.tone}`}>{s.value}</div>
              </div>
            ))}
          </div>

          {review.length > 0 && (
            <section className="mb-5 rounded-lg border border-amber-200 bg-amber-50/50 p-4">
              <h2 className="mb-2 font-serif text-lg font-semibold text-stone-800">{t("Needs review")}</h2>
              <ul className="divide-y divide-amber-100">
                {review.map((p) => (
                  <li key={p.id}>
                    <button onClick={() => setSelected(p.id)} data-testid={`trends-review-${p.id}`}
                      className="flex w-full flex-wrap items-center gap-2 py-2 text-left hover:bg-amber-50">
                      <span className="min-w-[160px] font-medium text-stone-800">{p.name || p.email}</span>
                      {p.flags.map((f) => <FlagChip key={f} f={f} />)}
                      <span className="ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-1">
                        {(p.tracker.symptoms.some((s) => s.flags.length)
                          ? p.tracker.symptoms.filter((s) => s.flags.length) : p.tracker.symptoms).map((s) => (
                          <span key={s.index} className="flex items-center gap-1.5 text-xs text-stone-500"
                            title={`${s.name}: ${fmt(s.baseline) || "—"} → ${fmt(s.latest) || "—"}`}>
                            <span className="max-w-[140px] truncate">{s.name}</span>
                            <Sparkline series={s.series} status={s.status} />
                            <b className="tabular-nums text-stone-700">{fmt(s.latest)}/10</b>
                          </span>
                        ))}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {sel && (
            <section className="mb-5 rounded-lg border border-[#E5DFD3] bg-white p-4" data-testid="trends-detail">
              <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-xl font-semibold text-stone-900">{sel.name || sel.email}</h2>
                  <div className="font-mono text-[11px] text-stone-400">
                    {sel.participant_code || sel.email} · {t(sel.assessment?.stage || "")}
                  </div>
                </div>
                <div className="flex gap-2">
                  {sel.assessment && (
                    <a href={`/assessment/${sel.assessment.id}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 rounded-md border border-[#E5DFD3] px-3 py-1.5 text-sm text-stone-700 hover:bg-stone-50">
                      <ExternalLink className="h-4 w-4" /> {t("Open assessment")}
                    </a>
                  )}
                  <button onClick={() => setSelected(null)} aria-label={t("Close")}
                    className="rounded-md border border-[#E5DFD3] p-1.5 text-stone-500 hover:bg-stone-50">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="mb-3 grid gap-2 sm:grid-cols-3 [&>*]:min-w-0">
                {sel.tracker.symptoms.map((s, i) => (
                  <div key={s.index} className="rounded-md border border-[#EFE9DE] px-3 py-2">
                    <div className="flex items-start gap-2 text-sm font-medium text-stone-800">
                      <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: LINE_COLORS[i] }} />
                      <span className="min-w-0 break-words">{s.name}</span>
                    </div>
                    <div className="mt-1 text-sm tabular-nums text-stone-600">
                      {fmt(s.baseline) || "—"} → <b>{fmt(s.latest) || "—"}</b>
                      {s.change_pct != null && <span className="ml-1 text-xs">({s.change_pct > 0 ? "+" : ""}{s.change_pct}%)</span>}
                      {s.status && <span className={`ml-2 text-xs font-semibold ${STATUS[s.status].cls}`}>{t(STATUS[s.status].label)}</span>}
                    </div>
                  </div>
                ))}
              </div>
              <TrendChart p={sel} />
              {sel.tracker.events.length > 0 && (
                <div className="mt-3">
                  <h3 className="mb-1 font-mono text-[11px] uppercase tracking-wider text-stone-400">{t("Triggers and notes")}</h3>
                  <ul className="space-y-1 text-sm text-stone-700">
                    {sel.tracker.events.map((e) => (
                      <li key={e.week}>
                        <b className="font-mono text-xs text-stone-500">{WEEKS[e.week]}</b>{" "}
                        {e.trigger}{e.trigger && e.note ? " — " : ""}<span className="text-stone-500">{e.note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          <div className="mb-3 flex flex-wrap items-center gap-3">
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="trends-search"
                placeholder={t("Search by name, code or email…")}
                className="w-full rounded-md border border-[#E5DFD3] bg-white py-2 pl-9 pr-3 text-sm focus:border-emerald-600 focus:outline-none" />
            </div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="trends-status"
              className="rounded-md border border-[#E5DFD3] bg-white px-3 py-2 text-sm">
              <option value="all">{t("All statuses")}</option>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{t(v.label)}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-sm text-stone-600">
              <input type="checkbox" checked={reviewOnly} onChange={(e) => setReviewOnly(e.target.checked)}
                className="h-4 w-4 accent-emerald-700" />
              {t("Needs review only")}
            </label>
            <span className="text-xs text-stone-500">{filtered.length} {t("of")} {tracking.length}</span>
          </div>

          <div className="overflow-x-auto rounded-lg border border-[#E5DFD3] bg-white" data-testid="trends-heatmap">
            <table className="w-full min-w-[900px] border-separate border-spacing-0 text-sm">
              <thead className="bg-[#E8EFE9]">
                <tr>
                  <th className="sticky left-0 z-10 bg-[#E8EFE9] px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-stone-500">
                    {t("Participant / symptom")}
                  </th>
                  {WEEKS.map((w) => (
                    <th key={w} className="px-1 py-2 text-center font-mono text-[11px] text-stone-500">{w}</th>
                  ))}
                  <th className="px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-stone-500">{t("Change")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => p.tracker.symptoms.map((s, i) => (
                  <tr key={`${p.id}-${s.index}`} onClick={() => setSelected(p.id)}
                    className={`cursor-pointer hover:bg-[#F6F8F5] ${selected === p.id ? "bg-[#F0F5F0]" : ""}`}>
                    <td className={`sticky left-0 z-10 bg-white px-3 py-1.5 ${i === 0 ? "border-t border-[#E5DFD3]" : ""}`}>
                      <div className="w-[196px]">
                        {i === 0 && (
                          <div className="flex items-center gap-2">
                            <span className="truncate font-medium text-stone-800" title={p.name || p.email}>{p.name || p.email}</span>
                            {p.flags.filter((f) => URGENT.has(f)).length > 0 && <span className="h-2 w-2 shrink-0 rounded-full bg-rose-600" />}
                          </div>
                        )}
                        <div className="truncate text-xs text-stone-500" title={s.name}>{s.name}</div>
                      </div>
                    </td>
                    {s.series.map((v, r) => (
                      <td key={r} className={`px-0.5 py-1 ${i === 0 ? "border-t border-[#E5DFD3]" : ""}`}>
                        <div title={`${s.name} — ${t(WEEK_LONG[r])}: ${v == null ? t("no entry") : fmt(v)}`}
                          className={`mx-auto flex h-7 w-9 items-center justify-center rounded text-xs font-semibold tabular-nums ${heat(v)}`}>
                          {v == null ? "·" : fmt(v)}
                        </div>
                      </td>
                    ))}
                    <td className={`whitespace-nowrap px-3 py-1.5 text-xs ${i === 0 ? "border-t border-[#E5DFD3]" : ""}`}>
                      {s.status ? (
                        <span className={`font-semibold ${STATUS[s.status].cls}`}>
                          {t(STATUS[s.status].label)}{s.change_pct != null && ` ${s.change_pct > 0 ? "+" : ""}${s.change_pct}%`}
                        </span>
                      ) : <span className="text-stone-400">—</span>}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-stone-500">
            <span>{t("Severity")}:</span>
            {[[0, "0–2"], [3, "3–4"], [5, "5–6"], [7, "7–8"], [9, "9–10"], [null, t("no entry")]].map(([v, l]) => (
              <span key={l} className="flex items-center gap-1">
                <span className={`inline-block h-3 w-4 rounded ${heat(v)}`} /> {l}
              </span>
            ))}
            <span className="ml-auto">{t("Improved = 30%+ lower than baseline · Worse = 20%+ higher")}</span>
          </div>
        </>
      )}
    </AppShell>
  );
}
